import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@shared/ui/pageHeader';
import { Tabs } from '@shared/ui/tabs';
import { ValueLabel } from '@features/ax/shared/ValueLabel';
import { Badge } from '@shared/ui/badge';
import { ClipboardList, Cpu, Smartphone, BarChart3, CheckCircle2, Clock, Camera, AlertTriangle } from 'lucide-react';
import { QuickActions } from './QuickActions';
import { Button } from '@shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shared/ui/dialog';
import { ReadsTab } from './tabs/ReadsTab';
import { SyncOverviewTab } from './tabs/SyncOverviewTab';
import { RoutesTab } from './tabs/RoutesTab';
import { MobileRoutesTab } from './tabs/MobileRoutesTab';
import { CycleDetailsTab } from './tabs/CycleDetailsTab';
import { NotesTab } from '@features/mx/shared/components/NotesTab';
import { ActivityLogTab } from '@features/mx/shared/components/ActivityLog';
import { useMeterRunDetail } from '../../hook';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { EstimateMissingReadingsDialog } from '../../../meterReads/components/PhotoMeterReads/dialogs/EstimateMissingReadingsDialog';
import { MarkAsCompleteDialog } from '../../../meterReads/components/PhotoMeterReads/dialogs/MarkAsCompleteDialog';
import { UploadMissingReadingsDialog } from './dialogs/UploadMissingReadingsDialog';
import { NoteDialog } from '@features/mx/shared/components/NoteDialog';
import { toast } from '@shared/ui/use-toast';
import { useDownloadTemplateOperations } from '../../../meterReads/hooks';
import BadgeList from '@shared/components/BadgeList';

export const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const utilityId = getRemoteUtilityId();
  const { type, runId } = useParams();

  const { mutate: downloadTemplate } = useDownloadTemplateOperations();

  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  const [showMarkCompleteDialog, setShowMarkCompleteDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showZeroResourceDialog, setShowZeroResourceDialog] = useState(false);

  // Determine read type from searchParams (default: manual)
    const readType = type === 'automated' ? 'automated' : type === 'mobile' ? 'mobile' : 'manual';


  // Map to the meter_type string the backend expects
  const meterTypeMap: Record<string, string> = {
    manual: 'MANUAL_METER',
    automated: 'SMART_METER',
    mobile: 'PHOTO_METER'
  };

  const { data: meterRunDetail, isLoading } = useMeterRunDetail(runId, utilityId, meterTypeMap[readType]);
  console.log(meterRunDetail,'run di')
  // Tab configurations per readType
  const { tabComponents, urlMapping, defaultTab } = useMemo(() => {
    if (readType === 'automated') {
      return {
        defaultTab: 'sync-overview',
        urlMapping: {
          'sync-overview': 'sync-overview',
          reads: 'reads',
          'cycle-details': 'cycle-details',
          notes: 'notes',
          'activity-logs': 'activity-logs',
        },
        tabComponents: {
          'sync-overview': {
            label: 'Sync Overview',
            shortLabel: 'sync-overview',
            component: <SyncOverviewTab />,
          },
          reads: {
            label: 'Reads',
            shortLabel: 'reads',
            component: <ReadsTab />,
          },
          'cycle-details': {
            label: 'Cycle Details',
            shortLabel: 'cycle-details',
            component: <CycleDetailsTab meterRunDetail={meterRunDetail} />,
          },
          notes: {
            label: 'Notes',
            shortLabel: 'notes',
            component: <NotesTab requestId={runId as string} source="Meter Run" />,
          },
          'activity-logs': {
            label: 'Activity Logs',
            shortLabel: 'activity-logs',
            component: <ActivityLogTab entityId={runId as string} entityType="6" />,
          },
        },
      };
    }

    if (readType === 'mobile') {
      return {
        defaultTab: 'routes',
        urlMapping: {
          routes: 'routes',
          reads: 'reads',
          'cycle-details': 'cycle-details',
          notes: 'notes',
          'activity-logs': 'activity-logs',
        },
        tabComponents: {
          routes: {
            label: 'Routes',
            shortLabel: 'routes',
            component: <MobileRoutesTab />,
          },
          reads: {
            label: 'Reads',
            shortLabel: 'reads',
            component: <ReadsTab />,
          },
          'cycle-details': {
            label: 'Cycle Details',
            shortLabel: 'cycle-details',
            component: <CycleDetailsTab meterRunDetail={meterRunDetail} />,
          },
          notes: {
            label: 'Notes',
            shortLabel: 'notes',
            component: <NotesTab requestId={runId as string} source="6" />,
          },
          'activity-logs': {
            label: 'Activity Logs',
            shortLabel: 'activity-logs',
            component: <ActivityLogTab entityId={runId as string} entityType="6" />,
          },
        },
      };
    }

    // Default: manual
    return {
      defaultTab: 'reads',
      urlMapping: {
        reads: 'reads',
        'cycle-details': 'cycle-details',
        routes: 'routes',
        notes: 'notes',
        'activity-logs': 'activity-logs',
      },
      tabComponents: {
        reads: {
          label: 'Reads',
          shortLabel: 'reads',
          component: <ReadsTab />,
        },
        'cycle-details': {
          label: 'Cycle Details',
          shortLabel: 'cycle-details',
          component: <CycleDetailsTab meterRunDetail={meterRunDetail} />,
        },
        routes: {
          label: 'Routes',
          shortLabel: 'routes',
          component: <RoutesTab />,
        },
        notes: {
          label: 'Notes',
          shortLabel: 'notes',
          component: <NotesTab requestId={runId as string} source="6" />,
        },
        'activity-logs': {
          label: 'Activity Logs',
          shortLabel: 'activity-logs',
          component: <ActivityLogTab entityId={runId as string} entityType="6" />,
        },
      },
    };
  }, [readType, runId, meterRunDetail]);

  const headerTitle =
    readType === 'mobile'
      ? 'View Mobile Read'
      : readType === 'automated'
        ? 'View Automated AMI Read'
        : 'View Manual Read';

  const headerDescription =
    readType === 'mobile'
      ? 'Track performance and progress of your mobile Read'
      : readType === 'automated'
        ? 'Track sync performance and records of your Automated Read'
        : 'Track cycle progress and field read records for this run.';

  const handleQuickAction = (action: string) => {
    if (action === 'Estimate Reads') {
      setShowEstimateDialog(true);
    } else if (action === 'Mark Complete') {
      const resourceCount = Number(meterRunDetail?.meterCount ?? 0);
      if (resourceCount === 0) {
        setShowZeroResourceDialog(true);
      } else {
        setShowMarkCompleteDialog(true);
      }
    } else if (action === 'View Cycle') {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'cycle-details');
      setSearchParams(newParams);
    } else if (action === 'View Routes') {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'routes');
      setSearchParams(newParams);
    } else if (action === 'Import Reads') {
      setShowUploadDialog(true);
    } else if (action === 'Add Notes') {
      setShowNoteDialog(true);
    } else if (action === 'Export Reads') {
      downloadTemplate({ remote_utility_id: utilityId, run_id: runId as string });
    } else if (action === 'Sync Overview') {
      toast({
        title: "Functionality available after integration",
      });
    } else if (action === 'View Vendor') {
      toast({
        title: "vendor not available",
      });
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Header with Navigation */}
      <PageHeader
        title={headerTitle}
        description={headerDescription}
        onBack={() => navigate('/mx/meterread/manual-meter?page=1&tab=all')}
      />

      {/* Main Hero Card & Quick Actions Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Run Details Card (3 cols) */}
        <div className="lg:col-span-3 bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
          {/* Card Top Row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-5">
            <div className="flex items-start gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                {readType === 'automated' ? (
                  <Cpu className="w-6 h-6" />
                ) : readType === 'mobile' ? (
                  <Smartphone className="w-6 h-6" />
                ) : (
                  <ClipboardList className="w-6 h-6" />
                )}
              </div>
              <div className="space-y-1">
                <h2 className="text-base font-bold text-gray-900">
                  {meterRunDetail?.meterSchedule?.meterCycle?.meterCycleName || 'N/A'}
                </h2>
                <div className="flex items-center gap-2 pt-0.5">
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-200 text-[11px] font-medium px-2 py-0.5 rounded-full"
                  >
                    {meterRunDetail?.month || 'N/A'}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-200 text-[11px] font-medium px-2 py-0.5 rounded-full"
                  >
                    {readType === 'automated'
                      ? 'Automated'
                      : readType === 'mobile'
                        ? 'Mobile'
                        : 'Manual'}
                  </Badge>
                  {readType === 'automated' && (
                    <Badge
                      variant="outline"
                      className="bg-gray-100 text-gray-700 border-gray-200 text-[11px] font-medium px-2 py-0.5 rounded-full"
                    >
                      AMI
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Run Stats Summary Box */}
            <div className="flex items-center gap-4 bg-gray-50/70 border border-gray-200 px-4 py-2.5 rounded-xl self-start sm:self-auto">
              <div className="space-y-0.5">
                <span className="text-[10px] text-gray-500 block">Status</span>
                <Badge
                  variant="outline"
                  className={`${meterRunDetail?.status === 'Completed'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-blue-50 text-blue-700 border-blue-200'
                    } text-xs font-medium px-2 py-0.5 rounded-full`}
                >
                  {meterRunDetail?.status || 'Pending'}
                </Badge>
              </div>
              <div className="h-7 w-px bg-gray-200" />
              <div className="space-y-0.5 text-center">
                <span className="text-[10px] text-gray-500 block">Total</span>
                <span className="text-base font-bold text-gray-900">
                  {meterRunDetail?.meterCount || 0}
                </span>
              </div>
              <div className="h-7 w-px bg-gray-200" />
              <div className="space-y-0.5 text-center">
                <span className="text-[10px] text-gray-500 block">
                  {readType === 'automated' ? 'Synced' : 'Completed'}
                </span>
                <span className="text-base font-bold text-emerald-600">
                  {meterRunDetail?.readingStats?.totalReadingCount || 0}
                </span>
              </div>
              <div className="h-7 w-px bg-gray-200" />
              <div className="space-y-0.5 text-center">
                <span className="text-[10px] text-gray-500 block">Pending</span>
                <span className="text-base font-bold text-amber-500">
                  {meterRunDetail?.missingReadingCount || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Section: Run Overview */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 tracking-wider">
              Run Overview
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
              <ValueLabel label="Resource Type" value={<BadgeList items={meterRunDetail?.meterSchedule?.meterCycle?.resourceTypeDisplay} />} />
              <ValueLabel
                label="Sub Type"
                value={<BadgeList items={meterRunDetail?.meterSchedule?.meterCycle?.resourceSubTypeDisplay} />}
              />
              <ValueLabel
                label="Total Routes"
                value={meterRunDetail?.routeCount || 0}
              />
              <ValueLabel
                label="Total Resources"
                value={meterRunDetail?.meterCount || 0}
              />
            </div>
          </div>

          {/* Section: Audit */}
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 tracking-wider">
              Audit
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
              {readType === 'automated' ? (
                <>
                  <ValueLabel label="Protocol" value={meterRunDetail?.protocol || 'N/A'} />
                  <ValueLabel label="Collection Frequency" value={meterRunDetail?.collectionFrequency || 'N/A'} />
                  <ValueLabel label="Run Time" value={meterRunDetail?.startDatetime || 'N/A'} />
                  <ValueLabel label="Next Run Date" value={meterRunDetail?.endDate || 'N/A'} />
                </>
              ) : (
                <>
                  <ValueLabel
                    label="Run By"
                    value={meterRunDetail?.meterSchedule?.createdUserRemoteName || 'N/A'}
                  />
                  <ValueLabel label="Run Date" value={meterRunDetail?.createdDate || 'N/A'} />
                  <ValueLabel
                    label="Run Time"
                    value={meterRunDetail?.startDatetime || 'N/A'}
                  />
                  <ValueLabel label="Next Run Date" value={meterRunDetail?.endDate || 'N/A'} />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions Component (1 col) */}
        <div className="lg:col-span-1">
          <QuickActions readType={readType} onAction={handleQuickAction} />
        </div>
      </div>

      {/* KPI Stats Bar (for Mobile Read Type) */}
      {/* {readType === 'mobile' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {meterRunDetail?.metricsPercentages?.completionRate ?? 0}%
              </p>
              <p className="text-[11px] text-gray-400">
                {(meterRunDetail?.metricsComparison?.completionRateChange ?? 0) >= 0 ? '+' : ''}{meterRunDetail?.metricsComparison?.completionRateChange ?? 0}% from last cycle
              </p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-center">
              <BarChart3 className="w-4 h-4" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500">Reading Accuracy</p>
              <p className="text-2xl font-bold text-gray-900">
                {meterRunDetail?.metricsPercentages?.readingAccuracy ?? 0}%
              </p>
              <p className="text-[11px] text-gray-400">
                {(meterRunDetail?.metricsComparison?.readingAccuracyChange ?? 0) >= 0 ? '+' : ''}{meterRunDetail?.metricsComparison?.readingAccuracyChange ?? 0}% from last cycle
              </p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500">Avg Turnaround</p>
              <p className="text-2xl font-bold text-gray-900">
                {meterRunDetail?.metricsPercentages?.avgTurnaround ?? 0}
              </p>
              <p className="text-[11px] text-gray-400">
                {(meterRunDetail?.metricsComparison?.avgTurnaroundChange ?? 0) >= 0 ? '+' : ''}{meterRunDetail?.metricsComparison?.avgTurnaroundChange ?? 0} from last cycle
              </p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500">Photo Submission</p>
              <p className="text-2xl font-bold text-purple-600">
                {meterRunDetail?.metricsPercentages?.photoSubmission ?? 0}%
              </p>
              <p className="text-[11px] text-gray-400">
                {(meterRunDetail?.metricsComparison?.photoSubmissionChange ?? 0) >= 0 ? '+' : ''}{meterRunDetail?.metricsComparison?.photoSubmissionChange ?? 0}% from last cycle
              </p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Camera className="w-4 h-4" />
            </div>
          </div>
        </div>
      )} */}

      {/* Dynamic Tabs Container */}
      <div className="w-full mt-4">
        <Tabs
          defaultValue={defaultTab}
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListClassName="grid grid-cols-5"
        />
      </div>

      {/* Dialogs */}
      <EstimateMissingReadingsDialog
        open={showEstimateDialog}
        setOpen={setShowEstimateDialog}
      />
      <MarkAsCompleteDialog
        open={showMarkCompleteDialog}
        setOpen={setShowMarkCompleteDialog}
      />
      <UploadMissingReadingsDialog
        open={showUploadDialog}
        setOpen={setShowUploadDialog}
      />
      <NoteDialog
        isOpen={showNoteDialog}
        onClose={() => setShowNoteDialog(false)}
        itemId={runId}
        source={readType === 'automated' ? 'Meter Run' : '6'}
        title="Add Note"
      />
      <Dialog open={showZeroResourceDialog} onOpenChange={setShowZeroResourceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
              <span>No Resources Available</span>
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-700 leading-relaxed">
              This run has <strong>0 resources</strong>, so job cards cannot be generated and it cannot be marked as complete.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowZeroResourceDialog(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default Landing;
